import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";
import { Logo } from "../brand/Logo";
import { useLang } from "../../i18n/LanguageContext";
import { LANGUAGES } from "../../i18n/translations";

export function PublicHeader() {
  const { t, lang, changeLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/products", label: t("nav.products") },
    { to: "/our-story", label: t("nav.story") },
    { to: "/recipes", label: t("nav.recipes") },
    { to: "/where-to-buy", label: t("nav.whereToBuy") },
    { to: "/trade", label: t("nav.trade") },
    { to: "/contact", label: t("nav.contact") },
  ];

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <header
      data-testid="public-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-sambiva-cream/95 backdrop-blur-md shadow-sm py-2" : "bg-sambiva-cream/80 backdrop-blur-sm py-3"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="shrink-0">
          <Logo className="h-9 sm:h-11" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-sm font-medium transition-colors relative ${
                isActive(l.to) ? "text-sambiva-red" : "text-sambiva-charcoal hover:text-sambiva-red"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1 text-sm">
            {LANGUAGES.map((l, i) => (
              <React.Fragment key={l}>
                {i > 0 && <span className="text-sambiva-muted/50">|</span>}
                <button
                  data-testid={`lang-${l}`}
                  onClick={() => changeLang(l)}
                  className={`px-1 font-semibold transition-colors ${
                    lang === l ? "text-sambiva-red" : "text-sambiva-muted hover:text-sambiva-charcoal"
                  }`}
                >
                  {l}
                </button>
              </React.Fragment>
            ))}
          </div>

          <Link
            to="/trade/login"
            data-testid="header-trade-login"
            className="hidden md:inline-flex text-sm font-semibold text-sambiva-green hover:text-sambiva-deepgreen transition-colors"
          >
            {t("nav.tradeLogin")}
          </Link>
          <Link
            to="/partner"
            data-testid="header-become-partner"
            className="hidden md:inline-flex items-center gap-1 bg-sambiva-red hover:bg-sambiva-deepred text-white text-sm font-semibold px-4 py-2 rounded-full transition-all hover:shadow-lg"
          >
            {t("nav.becomePartner")}
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-sambiva-charcoal p-1"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-sambiva-cream border-t border-sambiva-charcoal/10 mt-2 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <button
                key={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => navigate(l.to)}
                className={`flex items-center justify-between py-3 px-2 rounded-lg text-left font-medium ${
                  isActive(l.to) ? "text-sambiva-red bg-white" : "text-sambiva-charcoal"
                }`}
              >
                {l.label} <ChevronRight size={18} />
              </button>
            ))}
            <div className="flex items-center gap-3 py-3 px-2">
              {LANGUAGES.map((l) => (
                <button
                  key={l}
                  data-testid={`mobile-lang-${l}`}
                  onClick={() => changeLang(l)}
                  className={`px-2 py-1 rounded font-semibold text-sm ${
                    lang === l ? "bg-sambiva-red text-white" : "bg-white text-sambiva-charcoal"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
            <Link to="/trade/login" className="py-3 px-2 font-semibold text-sambiva-green">
              {t("nav.tradeLogin")}
            </Link>
            <Link
              to="/partner"
              className="mt-1 bg-sambiva-red text-white text-center font-semibold px-4 py-3 rounded-full"
            >
              {t("nav.becomePartner")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
